<script setup lang="ts">
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from '@headlessui/vue'
import { ChevronUpDownIcon } from '@heroicons/vue/16/solid'
import { CheckIcon } from '@heroicons/vue/20/solid'
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useUserStore } from '@/stores/user.store.ts'
import { storeToRefs } from 'pinia'
import { isEmpty, isNull, isString } from 'lodash'
import type { Project } from '@/components/projects/interfaces.ts'
import {
  getBuildHeaderNavigation,
  getNavigationUserMenuItems,
  LOCAL_STORAGE_CROWDY_API_ENV_KEY,
} from '@/config/config.ts'
import router from '@/router.ts'
import { Environment, InnerUrl, WebsocketConnectionState } from '@/enums.ts'
import { useTranslations } from '@/composables/useTranslations.ts'
import TopBar from '@/components/layouts/header/TopBar.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { HEADER_PADDING_TOP_PX, Z_INDEX_HEADER } from '@/config/visual.const.ts'
import { useUiStore } from '@/stores/ui.store.ts'
import { generateLinkForLogoClickRedirect } from '@/config/urls.const.ts'

const { t } = useTranslations()

const uiStore = useUiStore()
const { isStickyHeader } = storeToRefs(uiStore)

const route = useRoute()
const userStore = useUserStore()
const {
  projects,
  projectSelected,
  userInitials,
  userRoleId,
  userFirstName,
  userLastName,
  wsConnectionState,
} = storeToRefs(userStore)

const projectSelectedModel = ref(projectSelected.value)

const userNavigation = computed(() => getNavigationUserMenuItems(t))

const headerNavigation = computed(() =>
  getBuildHeaderNavigation(projectSelected.value, userRoleId.value, route, t),
)

watch<Project | null>(projectSelectedModel, (newProject, oldProject) => {
  if (!isNull(newProject)) {
    userStore.setCurrentProject(newProject)
    // redirect
    if (
      isString(route.params.projectKey) &&
      !isEmpty(route.params.projectKey) &&
      !isNull(oldProject)
    ) {
      router.push({
        path: route.path.includes(`${InnerUrl.Chats}/`) // TODO: Url const
          ? `/${newProject.key}${InnerUrl.Chats}`
          : route.path.replace(oldProject.key, newProject.key),
      })
    }
  }
})

watch(
  projectSelected,
  (newProject) => {
    projectSelectedModel.value = newProject
  },
  { immediate: true },
)

const environment = computed<Environment>(() => {
  return localStorage.getItem(LOCAL_STORAGE_CROWDY_API_ENV_KEY) as Environment
})

function wsConnectionStateToText(state: WebsocketConnectionState) {
  return (
    {
      [WebsocketConnectionState.Disconnected]: t('navigation.disconnected'),
      [WebsocketConnectionState.Connected]: t('navigation.connected'),
      [WebsocketConnectionState.Connecting]: `${t('navigation.connecting')}...`,
    }[state] ?? state
  )
}
</script>

<template>
  <Disclosure
    as="nav"
    :class="['mb-[70px] transition-all duration-300 ease-in-out', { 'md:sticky': isStickyHeader }]"
    v-slot="{ open }"
    :style="{
      zIndex: Z_INDEX_HEADER,
      paddingTop: `${HEADER_PADDING_TOP_PX}px`,
      top: isStickyHeader ? `-${HEADER_PADDING_TOP_PX}px` : 'auto',
    }"
  >
    <div class="absolute top-0 left-0">
      <img src="/images/circle.png" alt="circle" class="w-[43px] h-auto" />
    </div>
    <div
      :class="[
        'mx-auto bg-white shadow-sm pt-[13px] pb-[13px] pl-[69px] pr-[60px] relative transition-[border-radius] duration-500',
        {
          'md:rounded-[61px] rounded-0 max-w-[1535px]': !isStickyHeader,
          'w-full rounded-none': isStickyHeader,
        },
      ]"
    >
      <TopBar />
      <div class="flex sm:h-16 h-auto sm:flex-row flex-col items-center justify-between gap-3">
        <div class="flex items-center gap-3 flex-1 justify-between">
          <div class="flex items-center min-w-[120px] h-9 sm:h-auto">
            <RouterLink v-if="projectSelected" :to="generateLinkForLogoClickRedirect(projectSelected)">
              <img src="/crowdy-logo400rect.png" alt="Crowdy.ai" class="w-[238px] h-auto" />
            </RouterLink>
            <img v-else src="/crowdy-logo400rect.png" alt="Crowdy.ai" class="w-[238px] h-auto" />
          </div>
          <div class="hidden md:flex items-center space-x-2">
            <component
              v-for="item in headerNavigation"
              :is="item.disabled ? 'span' : 'router-link'"
              :to="item.href"
              :key="item.name"
              :class="[
                item.current
                  ? 'bg-yellowCustom text-white'
                  : 'text-darkBlue hover:bg-yellowCustom hover:text-white',
                'xl:text-[18px] font-medium text-sm rounded-[10px] xl:p-[11px_10px] p-[6px_8px]',
                item.disabled ? 'opacity-50 hover:cursor-pointer' : '',
              ]"
              :aria-current="item.current ? 'page' : undefined"
            >
              {{ item.name }}
            </component>
          </div>
        </div>
        <div class="flex items-center flex-row justify-between">
          <div class="flex items-center space-x-14 md:space-x-2 flex-row">
            <!-- Select project input-->
            <Listbox as="div" v-model="projectSelectedModel">
              <div class="relative">
                <ListboxButton
                  class="grid w-full min-w-[150px] cursor-pointer grid-cols-1 rounded-[25px] xl:py-[13px] xl:pl-5 xl:pr-4 py-2 px-2 pl-4 font-bold text-center xl:text-[15px] text-sm bg-yellowCustom text-darkBlue hover:bg-purpleCustom hover:text-yellowCustom"
                >
                  <span class="col-start-1 row-start-1 truncate pr-6">
                    {{ projectSelectedModel?.title ?? t('navigation.selectProject') }}
                  </span>
                  <ChevronUpDownIcon
                    class="col-start-1 row-start-1 size-5 self-center justify-self-end text-white sm:size-4"
                    aria-hidden="true"
                  />
                </ListboxButton>

                <transition
                  leave-active-class="transition ease-in duration-100"
                  leave-from-class="opacity-100"
                  leave-to-class="opacity-0"
                >
                  <ListboxOptions
                    style="z-index: 100;left:50%;margin-left:-120px;"
                    class="absolute mt-1 max-h-60 w-60 right-0 overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm"
                  >
                    <ListboxOption
                      as="template"
                      v-for="project in projects"
                      :key="project.id as number"
                      :value="project"
                      v-slot="{ active, selected }"
                    >
                      <li
                        :class="[
                          active ? 'bg-indigo-600 text-white outline-none' : 'text-gray-900',
                          'relative cursor-default select-none py-2 pl-3 pr-9',
                        ]"
                      >
                        <span
                          :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']"
                          >{{ project.title }}</span
                        >
                        <span
                          v-if="selected"
                          :class="[
                            active ? 'text-white' : 'text-indigo-600',
                            'absolute inset-y-0 right-0 flex items-center pr-4',
                          ]"
                        >
                          <CheckIcon class="size-5" aria-hidden="true" />
                        </span>
                      </li>
                    </ListboxOption>
                  </ListboxOptions>
                </transition>
              </div>
            </Listbox>

            <!-- Avatar circle -->
            <div class="flex items-center">
              <Menu as="div" class="relative">
                <div class="flex items-center gap-2">
                  <MenuButton
                    class="relative flex max-w-xs items-center text-white bg-fuchsiaCustom hover:text-purpleCustom hover:bg-yellowCustom p-2 xl:p-[13px] font-semibold rounded-full xl:text-[15px] text-sm focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-indigo-600 size-8 justify-center"
                  >
                    <span class="absolute -inset-1.5" />
                    <span class="sr-only">{{ t('navigation.openUserMenu') }}</span>
                    <span>{{ userInitials }}</span>
                  </MenuButton>
                  <div class="text-sm leading-4">
                    <p class="text-gray-700">{{ userFirstName }}</p>
                    <p class="text-gray-700">{{ userLastName }}</p>
                  </div>
                </div>
                <transition
                  enter-active-class="transition ease-out duration-100"
                  enter-from-class="transform opacity-0 scale-95"
                  enter-to-class="transform opacity-100 scale-100"
                  leave-active-class="transition ease-in duration-75"
                  leave-from-class="transform opacity-100 scale-100"
                  leave-to-class="transform opacity-0 scale-95"
                >
                  <MenuItems
                    style="z-index: 100"
                    class="absolute right-0 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none"
                  >
                    <span class="w-full text-center text-xs block font-medium align-middle py-1">
                      {{ userFirstName }} {{ userLastName }}
                    </span>
                    <MenuItem v-for="item in userNavigation" :key="item.name" v-slot="{ active }">
                      <RouterLink
                        :to="item.href"
                        :class="[
                          active ? 'bg-gray-100 outline-none' : '',
                          'block px-4 py-2 text-sm text-gray-700',
                        ]"
                      >
                        {{ item.name }}
                      </RouterLink>
                    </MenuItem>
                  </MenuItems>
                </transition>
              </Menu>
            </div>
          </div>

          <!-- Hamburger for mobile view -->
          <div class="md:hidden flex flex-auto">
            <DisclosureButton
              class="sm:ml-2 ml-0 p-2 text-gray-700 hover:text-indigo-600 flex items-center justify-center"
            >
              <font-awesome-icon
                v-if="!open"
                :icon="['fas', 'bars']"
                aria-hidden="true"
                class="text-purpleCustom w-6 h-6"
              />
              <font-awesome-icon
                v-else
                :icon="['fas', 'times']"
                aria-hidden="true"
                class="text-purpleCustom w-6 h-6"
              />
            </DisclosureButton>
          </div>
        </div>
      </div>
      <!-- Bottom bar -->
      <div
        class="absolute top-full -z-40 [zoom:0.8] md:[zoom:1] transition-all duration-500"
        :class="{
          'translate-y-0 opacity-100': !isStickyHeader,
          '-translate-y-24 opacity-0': isStickyHeader,
        }"
      >
        <span class="text-yellow-400 absolute text-[11px] left-3">
          {{ environment }} ({{ wsConnectionStateToText(wsConnectionState) }})
        </span>
        <p
          class="text-[17px] font-medium leading-[20.57px] text-left text-white bg-purpleCustom max-w-max px-[31px] py-[22px] rounded-b-[40px]"
        >
          {{ t('navigation.implementArtificial') }}...
        </p>
      </div>
    </div>

    <!-- Mob version drawer -->
    <DisclosurePanel
      :key="open"
      v-slot="{ open, close }"
      class="md:hidden"
      enter="transform transition ease-out duration-500"
      enter-from="translate-x-full"
      enter-to="translate-x-0"
      leave="transform transition ease-in duration-500"
      leave-from="translate-x-0"
      leave-to="translate-x-full"
    >
      <div class="fixed inset-0 z-50 flex">
        <div v-show="open" @click="close" class="fixed inset-0 bg-black/30 transition-opacity" />

        <div
          v-show="open"
          class="relative ml-auto w-full max-w-xs flex-1 flex-col bg-purpleCustom text-white pt-5 pb-4 h-full shadow-xl transform transition-transform duration-500 ease-out"
        >
          <div class="absolute right-4 top-4">
            <DisclosureButton class="p-2 text-gray-700 hover:text-indigo-600">
              <font-awesome-icon :icon="['fas', 'times']" class="text-white w-8 h-8" />
            </DisclosureButton>
          </div>

          <div class="mt-[120px] px-4 space-y-8 overflow-y-auto h-[calc(100%-120px)]">
            <div class="space-y-2">
              <component
                v-for="item in headerNavigation"
                :is="item.disabled ? 'span' : 'router-link'"
                :to="item.href"
                :key="item.name"
                @click="close"
                :class="[
                  item.current
                    ? 'bg-yellowCustom border-fuchsia text-white'
                    : 'border-transparent text-white hover:bg-gray-50 hover:border-gray-300 hover:text-gray-900',
                  'block px-3 py-2 rounded-md border-r-4 text-base font-medium ',
                  item.disabled ? 'opacity-50 hover:cursor-pointer' : '',
                ]"
              >
                {{ item.name }}
              </component>
            </div>
          </div>
        </div>
      </div>
    </DisclosurePanel>
  </Disclosure>
</template>

<style scoped></style>
